// review-queue-projection — the ONE rule that turns the pending payload into
// review items.
//
// The pending payload holds eighteen artefact arrays plus the
// `review_required` AgentTask rows. Two consumers must agree on what a review
// ITEM is: the review-queue page (`apps/web/src/lib/use-review-queue.ts`) and
// the sidebar / drawer badge (`ReviewQueueService.getCounts`). The rule used
// to be written twice, with a comment as the only bond, and the two copies
// held different task-type maps, so the badge and the page disagreed. Both now
// call `projectReviewQueue`, and the badge is a fold over its output
// (`tallyReviewQueue`), so they agree by construction.
//
// The module is a PRESENTATION projection, not a domain model (CONTEXT.md —
// "Client Application Architecture"). It imports no ontology runtime, which
// the contract-boundary guard makes unforgeable. `TASK_TYPE_FRONTEND` is
// therefore hand-kept here and held to the agent route table by a server-side
// parity guard: `apps/server/src/modules/review-queue/__tests__/
// task-surface-parity.test.ts`.
//
// It emits STRUCTURE only — the grain of an item, the frontend it belongs to,
// and the rows it carries. Labels, agent eyebrows and context links stay in
// `apps/web`, because only the web renders them.

import {
    REVIEWABLE_KIND_SPECS,
    kindsWithFrame,
    type AgentArtefactType,
    type ComposerDraftAgent,
    type ComposerPayloadKey,
    type ReviewFrontend,
} from './reviewable-kind-spec';
import type { AgentTaskType } from './agent-task-view';
import type { AgentRowTable } from './schemas/agent-rows';
import type { CoacActionDraftRow } from './contracts/coac';
import type {
    MonthlyNarrativeRow,
    RelationshipProposalRow,
    ReviewQueuePendingResponse,
} from './contracts/review-queue';

/** The four staff frontends a review item can belong to. Declared beside the
 *  kind table (`reviewable-kind-spec.ts`), because a kind's `surface` is one
 *  of them; re-exported here, where the projection reads it. */
export type { ReviewFrontend, ComposerPayloadKey };

/**
 * Which frontend a task type belongs to when its own row does not name a staff
 * frontend. A legacy AgentJob row can carry a surface outside the four (it
 * composes to `'global'`); the dispatcher no longer writes one, so this map is
 * the fallback for those rows and for the drawer link.
 *
 * The map is total over `AgentTaskType`, so the compiler refuses a new task
 * type with no entry. The server-side parity guard holds each entry to the
 * surface the agent declaration gives.
 *
 * The entries are PAIRS and not object keys. A task type is a snake_case
 * domain value, and the snake-case guard reads a bare snake key as a wire
 * field name.
 */
const TASK_TYPE_FRONTEND_ENTRIES = [
    ['post_call_processing', 'desk'],
    ['call_classification_batch', 'desk'],
    ['post_call_processing_batch', 'desk'],
    ['fact_find_prefill_batch', 'desk'],
    ['fact_find_document_extraction', 'desk'],
    ['suitability_document_extraction', 'desk'],
    ['suitability_check', 'comply'],
    ['monthly_report', 'ops'],
    ['monthly_narrative', 'desk'],
    ['kyc_renewal', 'comply'],
    ['breach_draft', 'comply'],
    ['sanctions_screening', 'comply'],
    ['consumer_duty_rescore', 'comply'],
    ['reconciliation_classification_batch', 'ops'],
    ['coac_pdf_extraction', 'ops'],
    // CEDAR's plain-English breakdown is read by the IM on the Desk decisions
    // inbox, although CEDAR itself is an Ops agent. The parity guard carries
    // this pair as a named exemption.
    ['coac_breakdown', 'desk'],
    ['suitability_letter_draft', 'desk'],
    ['fact_find_change_summary', 'desk'],
    ['onboarding_pack_draft', 'desk'],
    ['email_draft', 'desk'],
    ['onboarding_pipeline_watch', 'ops'],
    ['vulnerability_flag', 'comply'],
    ['framework_deviation', 'desk'],
    ['fund_policy_watch', 'view'],
    ['data_freshness_check', 'comply'],
    ['fund_rns_draft', 'view'],
    ['vulnerability_adaptation_plan', 'comply'],
    ['governance_paper_draft', 'view'],
    ['fair_value_assessment', 'comply'],
    ['workflow_run', 'ops'],
] as const satisfies ReadonlyArray<readonly [AgentTaskType, ReviewFrontend]>;

/** A compile error here means a new `AgentTaskType` has no review surface.
 *  Add its entry above; the parity guard then checks the surface you chose. */
type AssertNever<T extends never> = T;
export type EveryTaskTypeHasAReviewSurface = AssertNever<
    Exclude<AgentTaskType, (typeof TASK_TYPE_FRONTEND_ENTRIES)[number][0]>
>;

/** The surface map, keyed by the raw task-type string so a caller with an
 *  unknown legacy type simply gets `undefined`. */
export const TASK_TYPE_FRONTEND: ReadonlyMap<string, ReviewFrontend> = new Map(
    TASK_TYPE_FRONTEND_ENTRIES,
);

/** The seven brief-based composers whose `*Draft` rows render in the generic
 *  composition workbench (ADR-0010). Read off the kind table. */
export type { ComposerDraftAgent };

/**
 * One composer stream: the task type, the payload array, the workbench table,
 * the owning frontend and the row label, bound into one record.
 *
 * The record is now a PROJECTION of `REVIEWABLE_KIND_SPECS`, not a second
 * declaration of it. Four keyed tables used to hold these five facts, and they
 * had to agree by hand.
 */
export interface ComposerDraftSource {
    readonly kind: AgentArtefactType;
    readonly agent: ComposerDraftAgent;
    /** The AgentTask type that produces this draft. */
    readonly taskType: AgentTaskType;
    /** The array of the pending payload that holds the draft rows. */
    readonly payloadKey: ComposerPayloadKey;
    /** The `*_drafts` table the workbench re-reads the full row from. */
    readonly table: AgentRowTable;
    readonly frontend: ReviewFrontend;
    /** Short noun phrase for the row's secondary line. */
    readonly label: string;
}

export const COMPOSER_DRAFT_SOURCES: ReadonlyArray<ComposerDraftSource> = kindsWithFrame(
    'workbench',
).map((kind) => {
    const spec = REVIEWABLE_KIND_SPECS[kind];
    // A workbench kind always declares its payload array; the guard in
    // `reviewable-kind-spec.test.ts` refuses one that does not.
    const payloadKey = spec.payloadKey;
    if (!payloadKey) throw new Error(`composer kind ${kind} declares no payloadKey`);
    return {
        kind,
        // A workbench kind's `agent` IS a `ComposerDraftAgent`; the type is
        // that same projection, and the index read widens it back to string.
        agent: spec.agent as ComposerDraftAgent,
        taskType: spec.taskType,
        payloadKey,
        table: spec.rowTable as AgentRowTable,
        frontend: spec.surface,
        label: spec.label,
    };
});

const COMPOSER_SOURCE_BY_TASK_TYPE: ReadonlyMap<string, ComposerDraftSource> = new Map(
    COMPOSER_DRAFT_SOURCES.map((s) => [s.taskType as string, s]),
);

/**
 * The arrays of the pending payload that `projectReviewQueue` reads. The
 * other arrays (the detector findings) never make an item of their own: a
 * finding surfaces through its task.
 *
 * The shell badge reads only these arrays (`ReviewQueueService.getCounts`).
 * A test projects a payload with every other array filled, and it fails when
 * the projection starts to read one of them. Add the key here first.
 */
export const REVIEW_PROJECTION_PAYLOAD_KEYS: ReadonlySet<keyof ReviewQueuePendingResponse> =
    new Set<keyof ReviewQueuePendingResponse>([
        'callExtractedFacts',
        'callActionItems',
        'callKbNotes',
        'relationshipProposals',
        'wealthMonthlyNarrativeDrafts',
        'coacActionDrafts',
        ...COMPOSER_DRAFT_SOURCES.map((s) => s.payloadKey),
    ]);

/**
 * Task types whose artefacts already surface as their own item, so the task
 * envelope must not surface a second time. An AgentJob is the execution
 * envelope, never a human worklist item (CONTEXT.md).
 *
 * Post-call artefacts collapse into the per-call item, a monthly narrative
 * into its draft item, and one `coac_pdf_extraction` run into the N CA draft
 * items it produced. A detector finding has no item of its own and DOES
 * surface through its task, so no detector type belongs here.
 */
export const ARTEFACT_SURFACED_TASK_TYPES: ReadonlySet<string> = new Set<string>([
    'post_call_processing',
    'post_call_processing_batch',
    'monthly_narrative',
    'coac_pdf_extraction',
]);

/**
 * The task fields the projection reads. Both callers satisfy it: the web
 * passes the full `AgentTask`, and the server passes the slim row it maps out
 * of the composed task view.
 */
export interface ReviewProjectionTask {
    readonly id: string;
    readonly type: string | null;
    readonly status: string | null;
    /** The frontend of the row. Anything outside the four staff frontends
     *  falls back to `TASK_TYPE_FRONTEND`. */
    readonly frontend: string | null;
    readonly artefactId?: string | null;
}

interface ReviewEntryBase {
    /** Stable id for a React key and for the `?focus=` deep link. */
    readonly id: string;
    readonly frontend: ReviewFrontend;
    /** The approve or reject decisions this one item still carries. */
    readonly pendingCount: number;
}

/** One call's post-call artefacts, collapsed into a single item so the IM
 *  clears them together. */
export interface CallDraftsEntry extends ReviewEntryBase {
    readonly kind: 'call_drafts';
    readonly callId: string;
    readonly clientId: string | null;
    readonly clientName: string | null;
    /** The person the call resolved to (SEP-183). Null when it resolved to
     *  none, and null when the Contact sits outside the reader's book. */
    readonly contactId: string | null;
    readonly contactName: string | null;
    readonly factCount: number;
    readonly actionCount: number;
    readonly noteCount: number;
    /** Carried whole, because the payload already holds all the body renders
     *  (SEP-215). A proposal is a fourth decision on the call, not an item. */
    readonly proposals: RelationshipProposalRow[];
}

export interface MonthlyNarrativeEntry extends ReviewEntryBase {
    readonly kind: 'monthly_narrative';
    readonly draft: MonthlyNarrativeRow;
}

export interface CoacActionDraftEntry extends ReviewEntryBase {
    readonly kind: 'coac_action_draft';
    readonly draft: CoacActionDraftRow;
}

export interface ComposerDraftEntry<T extends ReviewProjectionTask> extends ReviewEntryBase {
    readonly kind: 'composer_draft';
    readonly draftId: string;
    readonly source: ComposerDraftSource;
    readonly clientId: string | null;
    /** The task envelope, when the item came from a task whose draft row has
     *  not yet propagated. Null when the draft row itself produced the item. */
    readonly task: T | null;
}

export interface TaskEntry<T extends ReviewProjectionTask> extends ReviewEntryBase {
    readonly kind: 'task';
    readonly task: T;
}

export type ReviewQueueEntry<T extends ReviewProjectionTask> =
    | CallDraftsEntry
    | MonthlyNarrativeEntry
    | CoacActionDraftEntry
    | ComposerDraftEntry<T>
    | TaskEntry<T>;

/** The server filters the payload to pending rows already. An unstamped row is
 *  still pending, so treat `null` as pending and drop anything else. */
function isPending(status: string | null | undefined): boolean {
    return status == null || status === 'pending';
}

function isReviewFrontend(value: string | null | undefined): value is ReviewFrontend {
    return value === 'desk' || value === 'ops' || value === 'comply' || value === 'view';
}

/**
 * Decide the frontend of a task row: its own staff frontend when it names one,
 * and the surface map otherwise. Exported because the agent drawer links to the
 * same board the queue puts the task on.
 */
export function frontendForTask(task: ReviewProjectionTask): ReviewFrontend | null {
    if (isReviewFrontend(task.frontend)) return task.frontend;
    if (!task.type) return null;
    return TASK_TYPE_FRONTEND.get(task.type) ?? null;
}

/**
 * Turn the pending payload and the `review_required` tasks into review items.
 *
 * One item is one unit of human review. The grain differs by kind: one call,
 * one narrative draft, one CA draft, one composer draft, one task. Each entry
 * carries the rows its body needs, so a caller adds only labels and links.
 */
export function projectReviewQueue<T extends ReviewProjectionTask>(
    payload: ReviewQueuePendingResponse,
    tasks: ReadonlyArray<T>,
): Array<ReviewQueueEntry<T>> {
    const out: Array<ReviewQueueEntry<T>> = [];

    // --- Post-call artefacts, one item for each call ---
    interface CallSlot {
        callId: string;
        clientId: string | null;
        clientName: string | null;
        contactId: string | null;
        contactName: string | null;
        factCount: number;
        actionCount: number;
        noteCount: number;
        proposals: RelationshipProposalRow[];
    }
    const callSlots = new Map<string, CallSlot>();
    const slotFor = (row: {
        callId: string;
        clientId?: string | null;
        clientName?: string | null;
        contactId?: string | null;
        contactName?: string | null;
    }): CallSlot => {
        const slot = callSlots.get(row.callId) ?? {
            callId: row.callId,
            clientId: null,
            clientName: null,
            contactId: null,
            contactName: null,
            factCount: 0,
            actionCount: 0,
            noteCount: 0,
            proposals: [],
        };
        // Every artefact of one call carries the same pair, so the first
        // non-null value wins.
        if (!slot.clientId && row.clientId) slot.clientId = row.clientId;
        if (!slot.clientName && row.clientName) slot.clientName = row.clientName;
        if (!slot.contactId && row.contactId) slot.contactId = row.contactId;
        if (!slot.contactName && row.contactName) slot.contactName = row.contactName;
        callSlots.set(row.callId, slot);
        return slot;
    };
    const tallyCall = (
        rows: ReadonlyArray<{
            callId: string;
            clientId?: string | null;
            clientName?: string | null;
            contactId?: string | null;
            contactName?: string | null;
            reviewStatus?: string | null;
        }>,
        kind: 'fact' | 'action' | 'note',
    ) => {
        for (const row of rows) {
            if (!isPending(row.reviewStatus) || !row.callId) continue;
            const slot = slotFor(row);
            if (kind === 'fact') slot.factCount += 1;
            else if (kind === 'action') slot.actionCount += 1;
            else slot.noteCount += 1;
        }
    };
    tallyCall(payload.callExtractedFacts, 'fact');
    tallyCall(payload.callActionItems, 'action');
    tallyCall(payload.callKbNotes, 'note');
    for (const row of payload.relationshipProposals) {
        if (!isPending(row.reviewStatus) || !row.callId) continue;
        slotFor(row).proposals.push(row);
    }
    for (const slot of callSlots.values()) {
        const pendingCount =
            slot.factCount + slot.actionCount + slot.noteCount + slot.proposals.length;
        if (pendingCount === 0) continue;
        out.push({
            kind: 'call_drafts',
            id: `call:${slot.callId}`,
            frontend: 'desk',
            pendingCount,
            callId: slot.callId,
            clientId: slot.clientId,
            clientName: slot.clientName,
            contactId: slot.contactId,
            contactName: slot.contactName,
            factCount: slot.factCount,
            actionCount: slot.actionCount,
            noteCount: slot.noteCount,
            proposals: slot.proposals,
        });
    }

    // --- Monthly narrative drafts. The IM reviews them, so they land on Desk.
    for (const draft of payload.wealthMonthlyNarrativeDrafts) {
        if (!isPending(draft.reviewStatus)) continue;
        out.push({
            kind: 'monthly_narrative',
            id: `narrative:${draft.id}`,
            frontend: 'desk',
            pendingCount: 1,
            draft,
        });
    }

    // --- CEDAR CA drafts. One CA PDF can give several, and each is an item.
    for (const draft of payload.coacActionDrafts) {
        if (!isPending(draft.reviewStatus)) continue;
        out.push({
            kind: 'coac_action_draft',
            id: `coac-draft:${draft.id}`,
            frontend: 'ops',
            pendingCount: 1,
            draft,
        });
    }

    // --- Composer `*_drafts` rows ---
    // A draft can reach the queue twice: as its own row, and through the task
    // envelope that produced it. The task can also arrive first, before the
    // row propagates. Emit each draft id once, whichever arrives first.
    const emittedDraftIds = new Set<string>();
    for (const source of COMPOSER_DRAFT_SOURCES) {
        for (const row of payload[source.payloadKey]) {
            if (!isPending(row.reviewStatus)) continue;
            emittedDraftIds.add(row.id);
            out.push({
                kind: 'composer_draft',
                id: `draft:${row.id}`,
                frontend: source.frontend,
                pendingCount: 1,
                draftId: row.id,
                source,
                clientId: row.clientId ?? null,
                task: null,
            });
        }
    }

    // --- AgentTask rows still waiting on review ---
    for (const task of tasks) {
        if (task.status !== 'review_required' || !task.type) continue;
        if (ARTEFACT_SURFACED_TASK_TYPES.has(task.type)) continue;

        const composer = COMPOSER_SOURCE_BY_TASK_TYPE.get(task.type);
        if (composer && task.artefactId) {
            if (emittedDraftIds.has(task.artefactId)) continue;
            emittedDraftIds.add(task.artefactId);
            out.push({
                kind: 'composer_draft',
                id: `draft:${task.artefactId}`,
                frontend: composer.frontend,
                pendingCount: 1,
                draftId: task.artefactId,
                source: composer,
                clientId: null,
                task,
            });
            continue;
        }

        const frontend = frontendForTask(task);
        if (!frontend) continue;
        out.push({
            kind: 'task',
            id: `task:${task.id}`,
            frontend,
            pendingCount: 1,
            task,
        });
    }

    return out;
}

export interface FrontendReviewTally {
    itemCount: number;
    decisionCount: number;
}

export type ReviewTallyByFrontend = Record<ReviewFrontend, FrontendReviewTally>;

/**
 * Fold the projection into per-frontend numbers for the shell badge. The badge
 * is therefore the same rule as the page, counted rather than rendered.
 */
export function tallyReviewQueue<T extends ReviewProjectionTask>(
    entries: ReadonlyArray<ReviewQueueEntry<T>>,
): ReviewTallyByFrontend {
    const out: ReviewTallyByFrontend = {
        desk: { itemCount: 0, decisionCount: 0 },
        ops: { itemCount: 0, decisionCount: 0 },
        comply: { itemCount: 0, decisionCount: 0 },
        view: { itemCount: 0, decisionCount: 0 },
    };
    for (const entry of entries) {
        out[entry.frontend].itemCount += 1;
        out[entry.frontend].decisionCount += entry.pendingCount;
    }
    return out;
}
